<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, shallowRef } from 'vue'
import { stateImages } from '@/utils/state-images'
import logoAnimationSrc from '@/assets/videos/logo-animation.mp4'

defineOptions({
  name: 'DreamaLoginLogo',
  inheritAttrs: false,
})

const videoReady = shallowRef(false)
const videoFailed = shallowRef(false)
const fallbackLocked = shallowRef(false)
const showVideo = computed(() => videoReady.value && !videoFailed.value && !fallbackLocked.value)
let fallbackTimer: number | null = null

function clearFallbackTimer() {
  if (fallbackTimer === null || typeof window === 'undefined') {
    fallbackTimer = null
    return
  }

  window.clearTimeout(fallbackTimer)
  fallbackTimer = null
}

function markVideoReady() {
  videoReady.value = true
  clearFallbackTimer()
}

function keepStaticLogo() {
  videoFailed.value = true
  clearFallbackTimer()
}

onMounted(() => {
  if (typeof window === 'undefined') {
    return
  }

  fallbackTimer = window.setTimeout(() => {
    fallbackTimer = null

    if (!videoReady.value) {
      fallbackLocked.value = true
    }
  }, 3000)
})

onBeforeUnmount(clearFallbackTimer)
</script>

<template>
  <div class="dreama-login-logo" aria-hidden="true">
    <img class="dreama-login-logo__static" :src="stateImages.appBrandLogo" alt="" />
    <video
      class="dreama-login-logo__video"
      :class="{ 'is-visible': showVideo }"
      :src="logoAnimationSrc"
      autoplay
      loop
      muted
      playsinline
      preload="auto"
      @loadeddata="markVideoReady"
      @playing="markVideoReady"
      @error="keepStaticLogo"
    />
  </div>
</template>

<style scoped>
.dreama-login-logo {
  position: relative;
  overflow: hidden;
  display: block;
  width: 88px;
  height: 88px;
  margin: 12px auto;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
}

.dreama-login-logo__static,
.dreama-login-logo__video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.dreama-login-logo__static {
  z-index: 0;
}

.dreama-login-logo__video {
  z-index: 1;
  opacity: 0;
  transition: opacity 0.18s ease;
}

.dreama-login-logo__video.is-visible {
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .dreama-login-logo__video {
    transition: none;
  }
}
</style>
